import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../../../api";
import { useLogos } from "../../../context/LogoProvider";

const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");
const IMAGE_UPLOAD_ENDPOINT = `${API_BASE}/upload/lesson-image`;

const ImageUploadModal = ({ open, mode = "new", initialSrc = "", onClose, onUploaded }) => {
    const { getSystemConfig } = useLogos();
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [remotePath, setRemotePath] = useState(initialSrc || "");
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedFile(null);
            setError("");
            setPreviewUrl("");
            setRemotePath(initialSrc || "");
        }
    }, [open, initialSrc]);

    if (!open) return null;

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setError("Sólo se permiten archivos de imagen.");
            return;
        }

        const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
        if (file.size > maxMB * 1024 * 1024) {
            setError(`La imagen no debe superar los ${maxMB}MB.`);
            return;
        }

        setError("");
        setSelectedFile(file);
        const blobUrl = URL.createObjectURL(file);
        setPreviewUrl(blobUrl);

        try {
            setUploading(true);

            // 🗑️ BORRAR IMAGEN ANTERIOR DEL S3 SI EXISTE
            if (initialSrc && initialSrc.includes('s3.amazonaws.com')) {
                try {
                    console.log('[ImageUploadModal] 🗑️ Eliminando imagen anterior de S3:', initialSrc);
                    const deleteResponse = await fetch(
                        `${IMAGE_UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(initialSrc)}`,
                        { method: 'DELETE' }
                    );
                    if (deleteResponse.ok) {
                        console.log('[ImageUploadModal] ✅ Imagen anterior eliminada de S3');
                    }
                } catch (deleteError) {
                    console.warn('[ImageUploadModal] ⚠️ Error al eliminar imagen anterior:', deleteError);
                }
            }

            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(IMAGE_UPLOAD_ENDPOINT, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Error al subir la imagen");
            }

            const data = await response.json();
            if (!data.filePath) {
                throw new Error("La respuesta del servidor no incluye filePath.");
            }

            console.log('[ImageUploadModal] ✅ Nueva imagen subida a S3:', data.filePath);
            setRemotePath(data.filePath);
        } catch (err) {
            console.error("Error al subir imagen:", err);
            setError(err.message || "Error al subir la imagen al servidor.");
            setSelectedFile(null);
            setPreviewUrl("");
            setRemotePath(initialSrc || "");
        } finally {
            setUploading(false);
        }
    };

    const handleConfirm = () => {
        if (!remotePath) {
            setError("Primero selecciona y sube una imagen.");
            return;
        }
        onUploaded?.(remotePath);
        onClose?.();
    };

    const imageUrl = previewUrl || (remotePath ? (remotePath.startsWith('http') ? remotePath : `${SERVER_URL}${remotePath}`) : "");

    return (
        <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                zIndex: 1000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: "min(640px, 96vw)",
                    maxHeight: "85vh",
                    background: "#fff",
                    borderRadius: 12,
                    boxShadow: "0 10px 30px rgba(0,0,0,.25)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <div
                    className="roles-topbar"
                    style={{
                        marginBottom: "0.6rem",
                        paddingRight: 16,
                        paddingLeft: 16,
                        paddingTop: 8,
                        paddingBottom: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flex: "0 0 auto",
                    }}
                >
                    <h3 className="roles-title" style={{ marginRight: 0 }}>
                        {mode === "new" ? "Agregar imagen" : "Editar imagen"}
                    </h3>
                    <button className="roles-btn" onClick={onClose} aria-label="Cerrar">
                        ✕
                    </button>
                </div>

                <div
                    style={{
                        padding: 16,
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        flex: "1 1 auto",
                        overflowY: "auto",
                    }}
                >
                    <div
                        className={`file-uploader${remotePath ? ' file-uploader--filled' : ''}${error ? ' file-uploader--error' : ''}${uploading ? ' file-uploader--uploading' : ''}`}
                    >
                        {remotePath && (
                            <div className="file-uploader__badge">
                                <span>✓</span>
                                <span>Archivo cargado</span>
                            </div>
                        )}

                        {imageUrl && (
                            <div className="file-uploader__preview">
                                <img src={imageUrl} alt="Vista previa" />
                            </div>
                        )}

                        <label className="file-uploader__label">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                onClick={(e) => { e.target.value = ''; }}
                                style={{ display: 'none' }}
                                disabled={uploading}
                            />
                            <div className="file-uploader__icon">
                                {uploading ? '⏳' : remotePath ? '🔄' : '🖼️'}
                            </div>
                            <div className="file-uploader__text">
                                {uploading ? 'Subiendo imagen...' : remotePath ? 'Cambiar imagen' : 'Haz clic para subir imagen'}
                            </div>
                            <div className="file-uploader__hint">
                                JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)
                            </div>
                        </label>
                    </div>

                    {error && (
                        <small style={{ color: "#b91c1c", fontSize: ".8rem" }}>{error}</small>
                    )}
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: ".6rem",
                        marginTop: ".8rem",
                        paddingRight: 16,
                        paddingBottom: 12,
                        flex: "0 0 auto",
                    }}
                >
                    <button className="roles-btn" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        className="btn-action"
                        onClick={handleConfirm}
                        disabled={uploading || !remotePath}
                        style={{
                            opacity: uploading || !remotePath ? 0.5 : 1,
                            cursor: uploading || !remotePath ? "not-allowed" : "pointer",
                        }}
                    >
                        {uploading ? "Subiendo..." : "Guardar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageUploadModal;
